import { html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import LightElement from './light-element.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import Helpers from '../../typescript/modules/helpers.js';
import RevealContent from '../../typescript/modules/reveal-content.js';
import SimpleBar from 'simplebar';
import Tooltip from '../../typescript/modules/tooltip.js';
import ShareLink from '../../typescript/modules/share-link.js';
import Tracking from '../../typescript/modules/tracking.js';

@customElement('product-comparison')
export default class ProductComparison extends LightElement {

	private productComparisonIdsKey: string = 'productComparisonIds';

	@property({ type: Array })
	productIds: string[] = [];

	@state()
	products: any[] = [];

	@state()
	scrolled: boolean = false;

	@state()
	collapsedGroups: Set<string> = new Set(); // Track collapsed groups by their DisplayName

	@state()
	highlightDifferences: boolean = false;

	@state()
	showBackButton: boolean = false;

	@state()
	backButtonURL: string = '/';

	@state()
	maxProducts: number = 5;

	@state()
	hasInvalidProducts: boolean = false;

	private simpleBarInstance: SimpleBar | null = null;

	private shareLinkHelper?: ShareLink;

	private _removeScrollListener?: () => void;

	private isValidProduct(product: any): boolean {
		return product && 
			product.Product && 
			product.Product.Id && 
			product.Product.DisplayName && 
			product.Product.SpecificationTable;
	}

	private get validProducts() {
		return this.products.filter(product => this.isValidProduct(product));
	}

	connectedCallback() {
		super.connectedCallback();
		this.shareLinkHelper = new ShareLink({
			shareTitle: 'My product comparison',
			shareText: 'View my product comparison:',
			trackingCategory: 'comparison',
			trackingMedium: 'link',
			trackingContent: 'comparison_link_share',
			trackingAction: 'Link',
			trackingLabel: 'Product Comparison',
			tooltipSelector: '.js-comparison-share-link-button-tooltip',
			shareId: 'product_comparison',
		});
		this.fetchProductDetails();
		this.setReferringUrl();
		document.addEventListener('productComparisonVariantSelected', this.handleVariantSelected.bind(this));
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		document.removeEventListener('productComparisonVariantSelected', this.handleVariantSelected.bind(this));

		if (this._removeScrollListener) {
			this._removeScrollListener();
		}

		if (this.simpleBarInstance) {
			this.simpleBarInstance.unMount();
			this.simpleBarInstance = null;
		}
	}

	async fetchProductDetails() {
		const invalidProductIds: string[] = [];
		
		const productPromises = this.productIds.map(async (productId) => {
			try {
				const response = await fetch(`/api/productviewer/getproduct/?id=${productId}&quantity=1`);
				if (!response.ok) {
					invalidProductIds.push(productId);
					return null;
				}
				const data = await response.json();
				
				// Validate that the response contains valid product data
				if (!this.isValidProduct(data)) {
					invalidProductIds.push(productId);
					return null;
				}
				
				return data;
			} catch (error) {
				console.error(`Error fetching product ${productId}:`, error);
				invalidProductIds.push(productId);
				return null;
			}
		});

		const results = await Promise.all(productPromises);
		
		this.products = results.filter(product => product !== null);

		if (invalidProductIds.length > 0) {
			this.hasInvalidProducts = true;
		}
		
		if (invalidProductIds.length > 0) {
			invalidProductIds.forEach(productId => {
				this.removeInvalidProductFromStorage(productId);
			});
			this.requestUpdate();
		} else {
			new RevealContent();

			setTimeout(() => {
				this.attachScrollObserver();
				this.initHorizontalScroll();
			}, 250);
		}
	}

	private removeInvalidProductFromStorage(productId: string) {
		const comparisonKey = this.productComparisonIdsKey;
		let comparisonIds = JSON.parse(localStorage.getItem(comparisonKey) || '[]');
		comparisonIds = comparisonIds.filter((id: string) => id !== productId);
		localStorage.setItem(comparisonKey, JSON.stringify(comparisonIds));
		this.productIds = comparisonIds;
	}

	private handleVariantSelected(event: Event) {

		const productToUpdate = (event as CustomEvent).detail.productId;
		const parentToRemove = (event as CustomEvent).detail.parentId;

		if (!productToUpdate) {
			return;
		}

		// If the parent is present, replace it with the variant at the same index
		if (parentToRemove) {
			const index = this.productIds.indexOf(parentToRemove);
			if (index !== -1) {
				this.productIds.splice(index, 1, productToUpdate);
			} else if (!this.productIds.includes(productToUpdate)) {
				// If parent not found, add variant if not already present
				this.productIds.push(productToUpdate);
			}
		} else if (!this.productIds.includes(productToUpdate)) {
			// If no parent, add variant if not already present
			this.productIds.push(productToUpdate);
		}

		const updateWishlistButtonsEvent = Helpers.createCustomEventForDispatch('updateWishlistButtons');
		document.dispatchEvent(updateWishlistButtonsEvent);

		localStorage.setItem(this.productComparisonIdsKey, JSON.stringify(this.productIds));
		this.fetchProductDetails();
	}

	private removeFromComparison(productId: string) {
		const comparisonKey = 'productComparisonIds';
		let comparisonIds = JSON.parse(localStorage.getItem(comparisonKey) || '[]');
		const productToRemove = this.products.find(product => product.Product.Id === productId);
		const productName = productToRemove?.Product.DisplayName;
		comparisonIds = comparisonIds.filter((id: string) => id !== productId);

		localStorage.setItem(comparisonKey, JSON.stringify(comparisonIds));

		this.productIds = comparisonIds;

		if (typeof this.trackRemoveComparisonProduct === 'function') {
			this.trackRemoveComparisonProduct(productId, productName);
		}

		const updatedEvent = Helpers.createCustomEventForDispatch('productComparisonUpdated');
		document.dispatchEvent(updatedEvent);
	}

	updated(changedProperties: Map<string | number | symbol, unknown>) {
		if (changedProperties.has('productIds')) {
			const oldProductIds = changedProperties.get('productIds') as string[];
			if (!this.areArraysEqual(oldProductIds, this.productIds)) {
				this.fetchProductDetails();
			}
			setTimeout(() => {
					const triggerUpdateWishlistButtons = Helpers.createCustomEventForDispatch('updateWishlistButtons');
					document.dispatchEvent(triggerUpdateWishlistButtons);
			}, 50);
		}
		Tooltip.init(false);

		if (this.productIds && this.productIds.length > 0) {
			this.updateShareLinkValue();
		}

		const shareButton = this.querySelector('.js-comparison-share-link-button') as HTMLButtonElement | null;
		const input = this.querySelector('.js-comparison-share-link-input') as HTMLInputElement | null;
		if (shareButton && input && this.shareLinkHelper && !shareButton.getAttribute('data-share-bound')) {
			shareButton.addEventListener('click', () => {
				this.shareLinkHelper?.shareUrl(input.value, input);
			});
			shareButton.setAttribute('data-share-bound', 'true');
		}
	}

	private updateShareLinkValue() {
		const ids = this.productIds || [];
		if (ids.length === 0) return;
		const base = `${location.origin}${location.pathname}`;
		const params = new URLSearchParams(location.search);
		params.set('ids', ids.join(','));
		const shareUrl = `${base}?${params.toString()}`;

		const input = this.querySelector('.js-comparison-share-link-input') as HTMLInputElement | null;
		if (input) {
			input.value = shareUrl;
		}
	}

	private areArraysEqual(arr1: string[] = [], arr2: string[] = []): boolean {
		if (arr1.length !== arr2.length) {
			return false;
		}
		return arr1.every((value, index) => value === arr2[index]);
	}

	private formatSpecValue(value: string, productId: string, productDisplayName: string) {
		if (value === 'Select an option first') {
			return html`<product-viewer-open-button productId="${productId}" productDisplayName="${productDisplayName}" text="${value}" isComparingMode></product-viewer-open-button>`;
		}
		return value;
	}

	private toggleGroup(groupName: string) {
		if (this.collapsedGroups.has(groupName)) {
			this.collapsedGroups.delete(groupName);
		} else {
			this.collapsedGroups.add(groupName);
		}
		this.requestUpdate();
	}

	private getAllUniqueSpecifications(): { groupName: string; items: string[] }[] {
		const allSpecifications: { [groupName: string]: Set<string> } = {};

		this.products.forEach((product) => {
			product.Product.SpecificationTable.forEach((group) => {
				if (!allSpecifications[group.DisplayName]) {
					allSpecifications[group.DisplayName] = new Set();
				}
				group.Items.forEach((item) => {
					allSpecifications[group.DisplayName].add(item.DisplayName);
				});
			});
		});

		return Object.entries(allSpecifications).map(([groupName, items]) => ({
			groupName,
			items: Array.from(items),
		}));
	}

	private isDifferent(groupName: string, itemName: string): boolean {
		const values = this.products.slice(0, this.maxProducts).map(product => {
			const spec = product.Product.SpecificationTable.find((g) => g.DisplayName === groupName)?.Items.find((i) => i.DisplayName === itemName);
			return spec ? this.formatSpecValue(spec.Value, product.Product.Id, product.Product.DisplayName) : '-';
		});
		return new Set(values).size > 1;
	}

	private isPriceDifferent(): boolean {
		const values = this.products.slice(0, this.maxProducts).map(product =>
			product.Product.Price?.Formatted ?? '-'
		);
		return new Set(values).size > 1;
	}

	private isRatingDifferent(): boolean {
		const values = this.products.slice(0, this.maxProducts).map(product =>
			`${product.Product.ReviewPercentage}|${product.Product.ReviewCount}|${product.Product.ReviewScore}`
		);
		return new Set(values).size > 1;
	}

	private isDeliveryDifferent(): boolean {
		const values = this.products.slice(0, this.maxProducts).map(product =>
			product.Product.DeliveryEstimate ?? '-'
		);
		return new Set(values).size > 1;
	}

	private isKeyFeaturesDifferent(): boolean {
		const featuresArr = this.products.slice(0, this.maxProducts).map(product =>
			(product.Product.KeyFeatures ?? []).join('|')
		);
		return new Set(featuresArr).size > 1;
	}

	private setReferringUrl() {
		const referringURL = document.referrer;
		if (referringURL && (referringURL.includes('/c/') || referringURL.includes('/p/'))) {
			this.backButtonURL = referringURL;
			this.showBackButton = true;
		} else {
			this.showBackButton = false;
		}
	}

	private attachScrollObserver() {
		const header = this.querySelector('.c-product-comparison__header');
		if (!header) return;

		const scrollDownThreshold = 200;
		const scrollUpThreshold = 150;
		let lastScrollTop = 0;
		let isTransitioning = false;
		let ticking = false;

		const requestTick = () => {
			if (!ticking) {
				requestAnimationFrame(checkScroll);
				ticking = true;
			}
		};

		const handleScroll = () => {
			requestTick();
			this.closeTooltips();
		};

		const checkScroll = () => {
			if (isTransitioning) {
				ticking = false;
				return;
			}

			const rect = header.getBoundingClientRect();
			const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
			const isScrollingDown = currentScrollTop > lastScrollTop;
			let shouldBeScrolled;

			if (isScrollingDown) {
				shouldBeScrolled = rect.top <= scrollDownThreshold;
			} else {
				shouldBeScrolled = rect.top <= scrollUpThreshold;
			}

			const stateChanged = this.scrolled !== shouldBeScrolled;

			if (stateChanged) {
				isTransitioning = true;
				this.scrolled = shouldBeScrolled;

				setTimeout(() => {
					isTransitioning = false;
				}, 100);
			}

			lastScrollTop = currentScrollTop;
			ticking = false;
		};

		window.addEventListener('scroll', handleScroll, { passive: true });

		checkScroll();

		this._removeScrollListener = () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}

	private initHorizontalScroll() {
		const headerContainer = this.querySelector('.js-product-comparison__header-wrapper') as HTMLElement | null;
		const tables = this.querySelectorAll('.js-product-comparison__table-container') as NodeListOf<HTMLElement>;
		if (!headerContainer || !tables) return;

		if (this.simpleBarInstance) return;

		this.simpleBarInstance = new SimpleBar(headerContainer, {
			autoHide: false,
			scrollbarMinSize: 25,
			clickOnTrack: true
		});

		const scrollContent = this.simpleBarInstance.getScrollElement();

		if (!scrollContent) {
			console.warn('SimpleBar scroll element not found');
			return;
		}

		const syncScroll = (sourceScrollLeft: number) => {
			if (scrollContent.scrollLeft !== sourceScrollLeft) {
				scrollContent.scrollLeft = sourceScrollLeft;
			}

			tables.forEach((table) => {
				if (table.scrollLeft !== sourceScrollLeft) {
					table.scrollLeft = sourceScrollLeft;
				}
			});
		};

		scrollContent.addEventListener('scroll', (e) => {
			const target = e.target as HTMLElement;
			syncScroll(target.scrollLeft);
			this.closeTooltips();
		}, { passive: true });

		tables.forEach((table) => {
			table.addEventListener('scroll', (e) => {
				const target = e.target as HTMLElement;
				syncScroll(target.scrollLeft);
				this.closeTooltips();
			}, { passive: true });
		});
	}

	private setHighlightDifferences(e: Event) {
		this.highlightDifferences = (e.target as HTMLInputElement).checked;
	}

	private closeTooltips() {
		Tooltip.hideTooltips();
		Tooltip.resetTriggers();
		if (Tooltip.dimmer()) {
			Tooltip.dimmer().classList.remove(Tooltip.dimmerVisibleClass());
		}
	}

	private trackRemoveComparisonProduct(productId: string, productName: string) {
		Tracking.customEvent("remove_comparison_product", {
			ecommerce: {
				items: [
					{
						item_id: productId,
						item_name: productName
					}
				]
			}
		});
	}

	render() {
		if (this.productIds.length === 0) {
			return html`
			<div class="c-product-comparison__empty" data-test="comparison-no-results-message">
				<div class="c-product-comparison__empty-content">
					<h2 class="c-product-comparison__empty-title">No products to compare</h2>
					<p class="c-product-comparison__empty-text">
						Visit a product category page or search for a product and select products to add them to your comparison.
					</p>
					<a href="/" class="c-button c-button--primary">
						Browse products
					</a>
				</div>
			</div>`;
		}

		if (this.products.length === 0) {
			return html`
			<div class="c-product-comparison__loading" data-test="comparison-loading">
				<span class="c-loader-round">
					<span class="u-visuallyhidden">Loading...</span>
				</span>
			</div>`;
		}

		if (this.validProducts.length === 1) {
			return html`
			<div class="c-product-comparison__empty" data-test="comparison-no-results-message">
				<div class="c-product-comparison__empty-content">
					<h2 class="c-product-comparison__empty-title">${this.hasInvalidProducts ? 'Not enough valid products to compare' : 'Only one product selected'}</h2>
					<p class="c-product-comparison__empty-text">
						${this.hasInvalidProducts ? 'Some products couldn\'t be loaded. You need at least 2 products to compare. Please add more products to your comparison.' : 'You need at least 2 products to compare. Please add more products to your comparison.'}
					</p>
					<a href="/" class="c-button c-button--primary">
						Browse products
					</a>
				</div>
			</div>`;
		}

		const uniqueSpecifications = this.getAllUniqueSpecifications();

		return html`

		<div class="c-product-comparison__header-actions">
			${this.showBackButton ?
				html`<a href="${this.backButtonURL}" class="c-button c-button--secondary">
					<svg viewBox="0 0 24 24" focusable="false" width="18" height="18" aria-hidden="true" class="c-icon c-product-comparison__header-actions--back-icon"><path fill="currentColor" fill-rule="evenodd" clip-rule="evenodd" d="m3.999 11.9997 8 8.0011 1.4143-1.4141-5.5859-5.5866H20v-2H7.8273l5.5871-5.5868-1.4142-1.4143-8.0012 8.0007z"></path></svg>
					Continue shopping
				</a>`
				: nothing}
			<label class="c-toggle">
				<span class="c-toggle__label">
					Show only differences
				</span>
				<input
					type="checkbox"
					class="c-toggle__input"
					.checked="${this.highlightDifferences}"
					@change="${this.setHighlightDifferences}"
				/>
				<span class="c-toggle__slider" data-test="show-differences-toggle"></span>
			</label>
		</div>

		<div class="c-product-comparison__products c-product-comparison__products--${this.validProducts.length.toString()}">

			<div class="c-product-comparison__header-wrapper js-product-comparison__header-wrapper">
				<div class="c-product-comparison__header js-product-comparison__header ${this.scrolled ? 'has-scrolled' : ''}"  data-test="comparison-header">
	
					${this.validProducts.slice(0, this.maxProducts).map(
						(product) => html`
							<div class="c-product-comparison__card" data-test="comparison-card" data-test-product-id="${product.Product.Id}">
								<div class="c-product-comparison__card-inner">
									<div class="c-product-comparison__card-top">
										<div class="c-product-comparison__card-image-container">
											<img src="${product.Product.Gallery.PrimaryGalleryItem.Url}?height=500&width=500&mode=crop&scale=both&quality=80" alt="${product.Product.DisplayName}" class="c-product-comparison__card-image" width="500" height="500" />
										</div>
										<div class="c-product-comparison__card-body">
											${this.validProducts.length > 2 ?
											html`<button
												class="u-link-button c-product-comparison__card-remove-button"
												data-test="comparison-remove-button"
												data-product-id="${product.Product.Id}"
											@click=${() => this.removeFromComparison(product.Product.Id)}
										>
											<span class="u-visuallyhidden">Remove</span>
											<img src="/images/icons/svgs/close.svg?v=20708" class="c-icon noPrint" width="28" height="28" loading="lazy" alt="" />
										</button>`
											: null}

											<div class="c-product-comparison__card-title"><a href="${product.Product.Url}" class="c-product-comparison__card-link">${product.Product.DisplayName}</a></div>
										</div>
									</div>

									<div class="c-product__price-wishlist-group" data-test="wishlist-group-${product.Product.Id}">
										${product.Product.HasVariants ?
										html`
											<div class="c-add-product" id="add-product-upsell-product-${product.Product.Id}">
												<product-viewer-open-button productId="${product.Product.Id}" productDisplayName="${product.Product.DisplayName}" isComparingMode showMessage text="Choose Options" data-test="product-viewer-choose-options-${product.Product.Id}"></product-viewer-open-button>
											</div>`
										: html`
											<product-add
												name="${product.Product.Id}"
												sku="${product.Product.Id}"
												displayName="${product.Product.DisplayName}"
												guid="${product.Product.Guid}"
												price="${product.Product.Price.Price}"
												isComparisonProduct
												viewItemEventInfo='${product.Product.ViewItemEventInfoJson}'
											></product-add>
										`}

										<wishlist-button productName="${product.Product.Id}" data-test="wishlist-btn-${product.Product.Id}"/>
									</div>
								</div>
							</div>
						`
					)}
				</div>
			</div>

			<div class="c-product-comparison__scroll js-product-comparison-scroll">

				<div class="c-product-comparison__table-wrapper">
					<div class="c-product-comparison__table-container js-product-comparison__table-container">
						<table class="c-table-striped c-table-striped--spec c-table-striped--comparison">
							<tbody>
								${(!this.highlightDifferences || this.isPriceDifferent()) ? html`
								<tr class="c-mobile-label-row">
									${this.validProducts.slice(0, this.maxProducts).map((product, index) => html`
										${index === 0 ? html`<td class="c-mobile-label"><span>Price</span></td>` : html`<td></td>`}
									`) }
								</tr>
								<tr class="c-product-comparison__table-row" data-test="comparison-row">
									<td>Price</td>
									${this.validProducts.slice(0, this.maxProducts).map((product) => html`
										<td>
											<product-price
												hideRrp="${product.Product.HideRrp || nothing}"
												showSaleWasPrice="${product.Product.ShowSaleWasPrice || nothing}"
												IsTradeCustomer="${product.Product.IsTradeCustomer || nothing}"
												rrp="${product.Product.RRP.Formatted}"
												retailPrice="${product.Product.RetailPrice.Formatted}"
												wasPrice="${product.Product.OriginalPrice.Formatted}"
												hasVariants="${product.Product.HasVariants || nothing}"
												price="${product.Product.Price.Formatted}"
											></product-price>
										</td>
									`) }
								</tr>
								` : nothing}

								${(!this.highlightDifferences || this.isRatingDifferent()) ? html`
								<tr class="c-mobile-label-row">
									${this.validProducts.slice(0, this.maxProducts).map((product, index) => html`
										${index === 0 ? html`<td class="c-mobile-label"><span>Rating</span></td>` : html`<td></td>`}
									`) }
								</tr>
								<tr class="c-product-comparison__table-row">
									<td>Rating</td>
									${this.validProducts.slice(0, this.maxProducts).map((product) => html`
										<td>
											<product-rating alwaysLarge reviewPercentage="${product.Product.ReviewPercentage}" reviewCount="${product.Product.ReviewCount}" reviewScore="${product.Product.ReviewScore}"></product-rating>
										</td>
									`) }
								</tr>
								` : nothing}

								${(!this.highlightDifferences || this.isDeliveryDifferent()) ? html`
								<tr class="c-mobile-label-row">
									${this.validProducts.slice(0, this.maxProducts).map((product, index) => html`
										${index === 0 ? html`<td class="c-mobile-label"><span>Delivery</span></td>` : html`<td></td>`}
									`) }
								</tr>
								<tr class="c-product-comparison__table-row">
									<td>Delivery</td>
									${this.validProducts.slice(0, this.maxProducts).map((product) => html`
										<td>
											<div class="c-product__delivery">
												<p class="c-delivery c-delivery--${product.Product.Stock.CssClass} c-delivery--large">${unsafeHTML(product.Product.DeliveryEstimate)}</p>
											</div>
										</td>
									`) }
								</tr>
								` : nothing}

								${(!this.highlightDifferences || this.isKeyFeaturesDifferent()) ? html`
								<tr class="c-mobile-label-row">
									${this.validProducts.slice(0, this.maxProducts).map((product, index) => html`
										${index === 0 ? html`<td class="c-mobile-label"><span>Key features</span></td>` : html`<td></td>`}
									`) }
								</tr>
								<tr class="c-product-comparison__table-row">
									<td>Key features</td>
									${this.validProducts.slice(0, this.maxProducts).map((product) => html`
										<td>
											${product.Product.KeyFeatures.length > 0 ?
												html`
													<div class="c-reveal ${product.Product.KeyFeatures.length > 0 ? 'c-reveal--active js-reveal-content' : ''}" data-test="description-reveal">
														<ul class="c-product-comparison__card-features">
															${product.Product.KeyFeatures.map(
																(feature) => html`
																	<li class="c-product-comparison__card-feature">
																		<span class="c-product-comparison__card-feature-text">${feature}</span>
																	</li>
																`)}
														</ul>
													</div>
												` : html`-`}
										</td>
									`) }
								</tr>
								` : nothing}
							</tbody>
						</table>
					</div>

					${uniqueSpecifications.map(
						(group) => {

							const visibleItems = group.items.filter(itemName => {
								return !this.highlightDifferences || this.isDifferent(group.groupName, itemName);
							});

							if (visibleItems.length === 0) {
								return nothing;
							}

							return html`
							<div class="c-product-comparison__accordion">
								<button
									class="c-table-striped__caption c-product-comparison__table-trigger c-accordion__title"
									data-test="accordion-trigger"
									@click=${() => this.toggleGroup(group.groupName)}
									aria-expanded="${!this.collapsedGroups.has(group.groupName)}"
								>
									${group.groupName ? group.groupName : 'Other'}
								</button>
								<div class="c-product-comparison__table-container js-product-comparison__table-container ${this.collapsedGroups.has(group.groupName) ? 'is-closed' : ''}" data-test="accordion-container">
									<table class="c-table-striped c-table-striped--spec c-table-striped--comparison" data-test="comparison-table">
										<tbody>
										${visibleItems
											.map(itemName => {
												const isDiff = this.isDifferent(group.groupName, itemName);
												// Find the first spec item to get description info
												const specItem = this.validProducts.find(product => 
													product.Product.SpecificationTable
														?.find(specGroup => specGroup.DisplayName === group.groupName)
														?.Items.find(item => item.DisplayName === itemName)
												)?.Product.SpecificationTable
													?.find(specGroup => specGroup.DisplayName === group.groupName)
													?.Items.find(item => item.DisplayName === itemName);

												return html`
													<tr class="c-mobile-label-row">
														${this.validProducts.slice(0, this.maxProducts).map((_, index) => html`
															${index === 0 ? html`<td class="c-mobile-label">
																<span>${itemName}
																	${specItem?.Description ? html`
																		<a href="#tooltip-${specItem.Name}"
																		class="js-tooltip-trigger"
																		data-js-tooltip-trigger
																		data-test="tooltip-trigger-mobile">
																			<tooltip-trigger-icon></tooltip-trigger-icon>
																			<span class="u-visuallyhidden">More information</span>
																		</a>
																	` : ''}
																</span></td>` : html`<td></td>`}
														`) }
													</tr>
													<tr class="c-product-comparison__table-row">
														<td>
															<span class="js-tooltip-trigger-wrapper">
																${itemName}
																${specItem?.Description ? html`
																	<a href="#tooltip-${specItem.Name}"
																	class="js-tooltip-trigger"
																	data-js-tooltip-trigger
																	data-test="tooltip-trigger">
																		<tooltip-trigger-icon></tooltip-trigger-icon>
																		<span class="u-visuallyhidden">More information</span>
																	</a>
																` : ''}
															</span>
														</td>
														${this.validProducts.slice(0, this.maxProducts).map((product) => {
															const spec = product.Product.SpecificationTable.find((specGroup) => specGroup.DisplayName === group.groupName)?.Items.find((item) => item.DisplayName === itemName);
															return html`<td class="${isDiff ? 'c-product-comparison__difference' : ''}">${spec ? this.formatSpecValue(spec.Value, product.Product.Id, product.Product.DisplayName) : '-'}</td>`;
														})}
													</tr>
												`;
											})
										}
										</tbody>
									</table>
								</div>
							</div>
							`
						}
					)}

					${uniqueSpecifications.map(group =>
						group.items.map(itemName => {
							const specItem = this.validProducts.find(product => 
								product.Product.SpecificationTable
									?.find(specGroup => specGroup.DisplayName === group.groupName)
									?.Items.find(item => item.DisplayName === itemName)
							)?.Product.SpecificationTable
								?.find(specGroup => specGroup.DisplayName === group.groupName)
								?.Items.find(item => item.DisplayName === itemName);

							return specItem?.Description ? html`
								<div class="js-tooltip"
									id="tooltip-${specItem.Name}"
									data-js-tooltip
									data-test="tooltip-overlay">
									<div class="js-tooltip__inner" data-js-tooltip-inner>
										<div class="cms">
											${unsafeHTML(specItem.Description)}
										</div>
									</div>
								</div>
							` : '';
						})
					)}
					<div class="c-product-comparison__table-container js-product-comparison__table-container" data-test="accordion-container">
						<table class="c-table-striped c-table-striped--spec c-table-striped--comparison" data-test="comparison-table">
							<tbody>
								<tr class="c-product-comparison__table-row c-product-comparison__table-row--last">
									<td>
									</td>
									${this.validProducts.slice(0, this.maxProducts).map((product) => {
										return html`<td>
											<div class="c-product__price-wishlist-group" data-test="wishlist-group-${product.Product.Id}">
												${product.Product.HasVariants ?
												html`
													<div class="c-add-product" id="add-product-upsell-product-${product.Product.Id}">
														<product-viewer-open-button productId="${product.Product.Id}" productDisplayName="${product.Product.DisplayName}" isComparingMode showMessage text="Choose Options" data-test="product-viewer-choose-options-${product.Product.Id}"></product-viewer-open-button>
													</div>`
												: html`
													<product-add
														name="${product.Product.Id}"
														sku="${product.Product.Id}"
														displayName="${product.Product.DisplayName}"
														guid="${product.Product.Guid}"
														price="${product.Product.Price.Price}"
														isComparisonProduct
														viewItemEventInfo='${product.Product.ViewItemEventInfoJson}'
													></product-add>
												`}

												<wishlist-button productName="${product.Product.Id}" data-test="wishlist-btn-${product.Product.Id}" />
											</div>
										</td>`;
									})}
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div><!-- /.c-product-comparison__scroll -->
			<div class="c-product-comparison__share">
				<label for="comparison-share-link" class="u-visuallyhidden">Comparison share link</label>
				<input id="comparison-share-link" class="c-form__input js-comparison-share-link-input" readonly data-test="comparison-share-link-input" value="" />
				<div class="c-product-comparison-share-link-button-tooltip__group">
					<div class="c-product-comparison-share-link-button__tooltip js-comparison-share-link-button-tooltip" aria-hidden="true" data-test="comparison-share-link-tooltip"></div>
					<button type="button" class="c-button c-button--dark c-button--dark--alt c-button--share-link js-comparison-share-link-button" data-test="comparison-share-link-share-button">Share</button>
				</div>
			</div>
		</div>`;
	}
}
